/**
 * Nine Finale Destruction Effect (九俱焚滅)
 * SS-tier special effect with meteor swarm convergence
 */

export interface Position {
  x: number;
  y: number;
}

interface MeteorTrailPoint {
  x: number;
  y: number;
}

interface Meteor {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  gravity: number;
  life: number;
  currentLife: number;
  trail: MeteorTrailPoint[];
  scale: number;
  targetX: number;
  targetY: number;
  assignedTarget: Position;
}

interface ExplosionSpark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  currentLife: number;
}

interface ShockwaveRing {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  life: number;
  currentLife: number;
}

export class NineFinaleEffect {
  private x: number;
  private y: number;
  private targetPositions: Position[];
  private meteors: Meteor[] = [];
  private sparks: ExplosionSpark[] = [];
  private rings: ShockwaveRing[] = [];
  private targetIndicators: Array<{x: number, y: number, life: number, maxLife: number}> = [];
  private duration: number = 1.8;
  private currentTime: number = 0;
  private isComplete: boolean = false;
  private screenShake: number = 0;
  private meteorHead: HTMLCanvasElement;

  constructor(x: number, y: number, targetPositions: Position[] = []) {
    this.x = x;
    this.y = y;
    this.targetPositions = targetPositions.length > 0 ? targetPositions : [{ x, y }];
    this.meteorHead = this.createMeteorSprite();
    this.initializeTargetIndicators();
    this.initializeMeteors();
  }

  private initializeTargetIndicators(): void {
    // Create anticipation indicators at target positions
    for (const target of this.targetPositions) {
      this.targetIndicators.push({
        x: target.x,
        y: target.y,
        life: 0.6, // Short indicator duration
        maxLife: 0.6,
      });
    }
  }

  private createMeteorSprite(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 96;
    canvas.height = 96;
    const ctx = canvas.getContext('2d')!;

    const gradient = ctx.createRadialGradient(48, 48, 0, 48, 48, 42);
    gradient.addColorStop(0, 'rgba(255,240,210,1)');
    gradient.addColorStop(0.35, 'rgba(255,180,140,1)');
    gradient.addColorStop(1, 'rgba(255,80,40,0.55)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(48, 48, 42, 0, Math.PI * 2);
    ctx.fill();

    return canvas;
  }

  private initializeMeteors(): void {
    // Create 1-2 meteors per target position for precise strikes
    for (let targetIndex = 0; targetIndex < this.targetPositions.length; targetIndex++) {
      const target = this.targetPositions[targetIndex];
      if (!target) {continue;}

      const meteorsPerTarget = Math.random() < 0.3 ? 2 : Math.random() < 0.7 ? 3 : 4; // 30% for 2, 40% for 3, 30% for 4

      for (let m = 0; m < meteorsPerTarget; m++) {
        // Varied spawn positions for dramatic effect
        const spawnAngle = Math.random() * Math.PI * 2;
        const spawnDistance = 400 + Math.random() * 300;

        const sx = target.x + Math.cos(spawnAngle) * spawnDistance;
        const sy = target.y + Math.sin(spawnAngle) * spawnDistance - 200; // Bias upward

        const dx = target.x - sx;
        const dy = target.y - sy;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;

        // Calculate speed accounting for heavier gravity for more impact
        const gravity = 250 + Math.random() * 150; // Increased for more weight
        const speed = 800 + Math.random() * 200;

        // Slight upward bias to account for gravity
        const gravityCompensation = gravity * 0.3;
        const adjustedVy = (dy / distance) * speed - gravityCompensation;

        this.meteors.push({
          x: sx,
          y: sy,
          vx: (dx / distance) * speed,
          vy: adjustedVy,
          rotation: Math.atan2(dy, dx),
          gravity,
          life: 1.8,
          currentLife: 0,
          trail: [],
          scale: 1.2 + Math.random() * 0.6, // Increased scale for larger meteors
          targetX: target.x,
          targetY: target.y,
          assignedTarget: target,
        });
      }
    }
  }

  private spawnExplosion(x: number, y: number): void {
    const sparkCount = 40 + Math.random() * 20; // More sparks for bigger impact
    for (let i = 0; i < sparkCount; i++) {
      this.sparks.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 2200, // Faster sparks
        vy: (Math.random() - 0.5) * 1800 - 200,
        life: 0.18 + Math.random() * 0.17,
        currentLife: 0,
      });
    }

    this.rings.push({
      x,
      y,
      radius: 0,
      maxRadius: 280, // Larger shockwave
      life: 0.45,
      currentLife: 0,
    });

    this.screenShake = Math.min(15, this.screenShake + 6.5); // More screen shake
  }

  public update(deltaTime: number): void {
    this.currentTime += deltaTime;

    if (this.currentTime >= this.duration) {
      this.isComplete = true;
      return;
    }

    this.screenShake = Math.max(0, this.screenShake - deltaTime * 18);

    this.updateMeteors(deltaTime);
    this.updateSparks(deltaTime);
    this.updateRings(deltaTime);
    this.updateTargetIndicators(deltaTime);
  }

  private updateMeteors(deltaTime: number): void {
    for (let i = this.meteors.length - 1; i >= 0; i--) {
      const meteor = this.meteors[i];
      if (!meteor) {continue;}

      meteor.currentLife += deltaTime;

      // Apply gravity
      meteor.vy += meteor.gravity * deltaTime;

      // Add slight homing behavior (course correction)
      const distanceToTarget = Math.sqrt(
        (meteor.targetX - meteor.x) ** 2 + (meteor.targetY - meteor.y) ** 2,
      );

      if (distanceToTarget > 20 && meteor.currentLife < meteor.life * 0.8) {
        // Apply gentle course correction for first 80% of flight
        const homingStrength = 0.1; // 10% course correction
        const targetDx = meteor.targetX - meteor.x;
        const targetDy = meteor.targetY - meteor.y;
        const targetDistance = Math.sqrt(targetDx * targetDx + targetDy * targetDy) || 1;

        const homingVx = (targetDx / targetDistance) * 600;
        const homingVy = (targetDy / targetDistance) * 600;

        meteor.vx = meteor.vx * (1 - homingStrength) + homingVx * homingStrength;
        meteor.vy = meteor.vy * (1 - homingStrength) + homingVy * homingStrength;

        // Update rotation to match new direction
        meteor.rotation = Math.atan2(meteor.vy, meteor.vx);
      }

      // Update position
      meteor.x += meteor.vx * deltaTime;
      meteor.y += meteor.vy * deltaTime;

      meteor.trail.push({ x: meteor.x, y: meteor.y });
      if (meteor.trail.length > 10) {
        meteor.trail.shift();
      }

      // Check hit with assigned target (tighter detection)
      const distanceToAssignedTarget = Math.sqrt(
        (meteor.x - meteor.assignedTarget.x) ** 2 +
        (meteor.y - meteor.assignedTarget.y) ** 2,
      );

      if (distanceToAssignedTarget < 20 || meteor.currentLife > meteor.life) {
        this.spawnExplosion(meteor.x, meteor.y);
        this.meteors.splice(i, 1);
      }
    }
  }

  private updateSparks(deltaTime: number): void {
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      if (!spark) {continue;}

      spark.currentLife += deltaTime;

      if (spark.currentLife > spark.life) {
        this.sparks.splice(i, 1);
        continue;
      }

      spark.vy += 1400 * deltaTime;
      spark.x += spark.vx * deltaTime;
      spark.y += spark.vy * deltaTime;
    }
  }

  private updateRings(deltaTime: number): void {
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      if (!ring) {continue;}

      ring.currentLife += deltaTime * 2.2;

      if (ring.currentLife >= ring.life) {
        this.rings.splice(i, 1);
        continue;
      }

      ring.radius = 24 + (ring.currentLife / ring.life) * ring.maxRadius;
    }
  }

  private updateTargetIndicators(deltaTime: number): void {
    for (let i = this.targetIndicators.length - 1; i >= 0; i--) {
      const indicator = this.targetIndicators[i];
      if (!indicator) {continue;}

      indicator.life -= deltaTime;

      if (indicator.life <= 0) {
        this.targetIndicators.splice(i, 1);
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (this.isComplete) {return;}

    const age = this.currentTime / this.duration;
    const fadeIn = Math.min(1, age / 0.12);
    const fadeOut = Math.min(1, (this.duration - this.currentTime) / 0.25);
    const alpha = Math.min(fadeIn, fadeOut);

    ctx.save();

    if (this.screenShake > 0) {
      const shakeX = (Math.random() - 0.5) * this.screenShake * 2;
      const shakeY = (Math.random() - 0.5) * this.screenShake * 2;
      ctx.translate(shakeX, shakeY);
    }

    this.renderTargetIndicators(ctx, alpha);
    this.renderMeteors(ctx, alpha);
    this.renderRings(ctx, alpha);
    this.renderSparks(ctx, alpha);

    ctx.restore();
  }

  private renderMeteors(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const meteor of this.meteors) {
      this.renderMeteorTrail(ctx, meteor, alpha);
      this.renderMeteorHead(ctx, meteor, alpha);
    }

    ctx.restore();
  }

  private renderMeteorTrail(ctx: CanvasRenderingContext2D, meteor: Meteor, alpha: number): void {
    if (meteor.trail.length < 2) {return;}

    for (let i = 0; i < meteor.trail.length - 1; i++) {
      const current = meteor.trail[i];
      const next = meteor.trail[i + 1];
      if (!current || !next) {continue;}

      const progress = i / (meteor.trail.length - 1);
      const width = 35 * (1 - progress); // Thicker trails for more impact

      ctx.globalAlpha = alpha * 0.35 * (1 - progress);
      ctx.strokeStyle = 'rgba(255,90,50,0.35)';
      ctx.lineWidth = width;
      ctx.lineCap = 'round';

      ctx.beginPath();
      ctx.moveTo(current.x, current.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
    }
  }

  private renderMeteorHead(ctx: CanvasRenderingContext2D, meteor: Meteor, alpha: number): void {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(meteor.x, meteor.y);
    ctx.rotate(meteor.rotation);
    ctx.scale(meteor.scale, meteor.scale);
    ctx.drawImage(this.meteorHead, -48, -48, 96, 96); // Larger meteor sprite rendering
    ctx.restore();
  }

  private renderSparks(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const spark of this.sparks) {
      const life = 1 - (spark.currentLife / spark.life);
      ctx.globalAlpha = alpha * life;
      ctx.strokeStyle = 'rgba(255,210,160,0.95)';
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.moveTo(spark.x - 2, spark.y);
      ctx.lineTo(spark.x + 2, spark.y);
      ctx.moveTo(spark.x, spark.y - 2);
      ctx.lineTo(spark.x, spark.y + 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private renderRings(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const ring of this.rings) {
      const life = 1 - (ring.currentLife / ring.life);
      ctx.globalAlpha = alpha * life;
      ctx.strokeStyle = 'rgba(255,140,90,0.7)';
      ctx.lineWidth = 3 + 7 * life;

      ctx.beginPath();
      ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  public getScreenShake(): number {
    return this.screenShake;
  }

  private renderTargetIndicators(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const indicator of this.targetIndicators) {
      const life = indicator.life / indicator.maxLife;
      const pulse = 0.5 + 0.5 * Math.sin(this.currentTime * 15); // Fast pulse

      ctx.globalAlpha = alpha * life * pulse;

      // Draw crosshair target indicator
      ctx.strokeStyle = 'rgba(255,100,50,0.8)';
      ctx.lineWidth = 2;

      const size = 15 + (1 - life) * 10; // Expand as it fades

      ctx.beginPath();
      ctx.moveTo(indicator.x - size, indicator.y);
      ctx.lineTo(indicator.x + size, indicator.y);
      ctx.moveTo(indicator.x, indicator.y - size);
      ctx.lineTo(indicator.x, indicator.y + size);
      ctx.stroke();

      // Draw outer ring
      ctx.beginPath();
      ctx.arc(indicator.x, indicator.y, size * 0.7, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  public isFinished(): boolean {
    return this.isComplete;
  }
}